import { Dialog } from "@base-ui/react/dialog";
import type { EnvironmentId } from "@amoreg/contracts";
import { filePreviewKind } from "@amoreg/shared/filePreview";
import { FileTextIcon, FilmIcon, Music2Icon, XIcon } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { formatAttachmentSize } from "../chat/attachmentFiles";

const Preview = lazy(() =>
  import("./AttachmentFilePreview").then((module) => ({ default: module.AttachmentFilePreview })),
);

const chipTriggerClass =
  "flex min-w-0 items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-accent disabled:opacity-60";

/**
 * Draft and persisted files share a viewer; persisted bytes always require a
 * fresh asset claim.
 *
 * With `onOpen` the chip opens the file where every other file opens: the
 * right panel (`rightPanelStore.openAttachment`); every chip in a
 * conversation, draft included, passes it. Without it — a caller with no
 * panel to open into, such as a fixture — it falls back to a modal preview.
 */
export function AttachmentChip(props: {
  attachment: { id: string; name: string; mimeType: string; sizeBytes: number };
  file?: Blob;
  disabled?: boolean;
  environmentId?: EnvironmentId;
  onRemove?: () => void;
  onOpen?: () => void;
}) {
  const { attachment } = props;
  const kind = filePreviewKind(attachment);
  const Icon = kind === "video" ? FilmIcon : kind === "audio" ? Music2Icon : FileTextIcon;
  const disabled = props.disabled || (!props.file && !props.environmentId);
  const label = (
    <>
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="max-w-48 truncate" title={attachment.name}>
        {attachment.name}
      </span>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {formatAttachmentSize(attachment.sizeBytes)}
      </span>
    </>
  );
  const remove = props.onRemove ? (
    <button
      type="button"
      aria-label={`Remove ${attachment.name}`}
      className="mr-1 rounded p-1 hover:bg-accent"
      onClick={props.onRemove}
    >
      <XIcon className="size-3.5" />
    </button>
  ) : null;

  if (props.onOpen) {
    return (
      <span className="inline-flex max-w-full items-center rounded-lg border border-border/80 bg-background/70 text-sm">
        <button
          type="button"
          disabled={disabled}
          className={chipTriggerClass}
          aria-label={`Open ${attachment.name}`}
          onClick={props.onOpen}
        >
          {label}
        </button>
        {remove}
      </span>
    );
  }
  return <AttachmentChipDialog {...props} disabled={disabled} label={label} remove={remove} />;
}

function AttachmentChipDialog(props: {
  attachment: { id: string; name: string; mimeType: string; sizeBytes: number };
  file?: Blob;
  disabled: boolean;
  environmentId?: EnvironmentId;
  onRemove?: () => void;
  label: React.ReactNode;
  remove: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { attachment } = props;
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <span className="inline-flex max-w-full items-center rounded-lg border border-border/80 bg-background/70 text-sm">
        <Dialog.Trigger
          disabled={props.disabled}
          className={chipTriggerClass}
          aria-label={`Preview ${attachment.name}`}
        >
          {props.label}
        </Dialog.Trigger>
        {props.remove}
      </span>
      {open ? (
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60" />
          <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 flex h-[85dvh] w-[94vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border bg-background shadow-xl">
            <Dialog.Title className="sr-only">Preview {attachment.name}</Dialog.Title>
            <Dialog.Description className="sr-only">Attached file preview</Dialog.Description>
            <Suspense
              fallback={
                <div role="status" className="p-4">
                  Loading preview…
                </div>
              }
            >
              <Preview
                key={JSON.stringify([props.environmentId, attachment.id])}
                name={attachment.name}
                mimeType={attachment.mimeType}
                sizeBytes={attachment.sizeBytes}
                {...(props.file ? { file: props.file } : {})}
                {...(props.environmentId
                  ? { asset: { environmentId: props.environmentId, attachmentId: attachment.id } }
                  : {})}
                onClose={() => setOpen(false)}
                {...(props.onRemove
                  ? {
                      onRemove: () => {
                        setOpen(false);
                        props.onRemove?.();
                      },
                    }
                  : {})}
              />
            </Suspense>
          </Dialog.Popup>
        </Dialog.Portal>
      ) : null}
    </Dialog.Root>
  );
}
